import React, { useState, useEffect } from 'react';
import {
  VStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  Badge,
  SimpleGrid,
  useColorModeValue,
  Heading,
  Avatar,
  Flex,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Input,
  FormControl,
  FormLabel,
  Select,
  Spacer,
  InputGroup,
  InputLeftElement,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Tooltip,
  AvatarGroup,
  Textarea,
  Switch,
  Checkbox,
} from '@chakra-ui/react'; 
import { motion } from 'framer-motion';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../context/AuthContext';
import UserDetailModal from '../components/UserDetailModal';
import { LineChart, PieChart, BarChart } from '../components/ChartCard';
import { FiUsers, FiUserPlus, FiEdit2, FiTrash2, FiPlus, FiPlusCircle, FiMinusCircle, FiSearch, FiMessageSquare } from 'react-icons/fi';
import { ResponsiveContainer } from 'recharts'; 
import { CartesianGrid, XAxis, YAxis, Legend, Line, Pie, Cell, Bar } from 'recharts';
import UserDashboardModal from './UserDashboardModal';
import KnowledgeBaseManager from '../components/KnowledgeBaseManager';
import { Users, Shield, Settings, UserCheck } from 'lucide-react';

const MotionCard = motion(Card);

// Chart colors
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

// Dummy data for demonstration
const dummyData = {
  totalVendors: 15,
  totalRevenue: 12400,
  activeUsers: 150,
  churnRate: 2.1,
  vendors: [
    { id: 1, name: 'Vendor A', status: 'active', revenue: 5000, users: 50, lastActivity: '2024-03-20' },
    { id: 2, name: 'Vendor B', status: 'inactive', revenue: 2000, users: 20, lastActivity: '2024-03-18' },
    { id: 3, name: 'Vendor C', status: 'active', revenue: 5400, users: 80, lastActivity: '2024-03-21' },
    // ... more vendors
  ],
  admins: [
    { id: 1, name: 'Admin One', email: 'admin1@example.com', status: 'active', role: 'admin', lastLogin: '2024-03-21T09:00:00' },
    // ... more admins
  ],
  users: [
    {
      id: 1,
      name: 'Regular User',
      email: 'user@example.com',
      vendor: 'Tech Solutions Inc',
      status: 'active',
      lastActive: '2024-03-20T10:30:00',
      usage: 75,
      createdAt: '2024-01-15T00:00:00',
      role: 'user',
      preferences: {
        notifications: true,
        theme: 'light',
      },
      purchaseHistory: [
         { id: 1, item: 'Product 1', amount: 50, date: '2024-03-15' },
         { id: 2, item: 'Product 2', amount: 75, date: '2024-03-20' },
      ]
    },
    // Add more users with similar structure
     {
      id: 2,
      name: 'Another User',
      email: 'anotheruser@example.com',
      vendor: 'Innovate Ltd',
      status: 'inactive',
      lastActive: '2024-03-10T14:00:00',
      usage: 30,
      createdAt: '2023-11-01T00:00:00',
      role: 'user',
      preferences: {
        notifications: false,
        theme: 'dark',
      },
       purchaseHistory: [
         { id: 3, item: 'Service X', amount: 150, date: '2024-03-05' },
      ]
    },
  ],
};

// Add these new chart data structures after the existing dummyData
const userStatsData = {
  tokenUsage: [
    { month: 'Jan', total: 10000, used: 8000 },
    { month: 'Feb', total: 12000, used: 9500 },
    { month: 'Mar', total: 15000, used: 12000 },
    { month: 'Apr', total: 13000, used: 11000 },
    { month: 'May', total: 17000, used: 14000 },
  ],
  agentUsage: [
    { name: 'Chat Bot', count: 1200 },
    { name: 'Voice Agent', count: 800 },
  ],
  monthlyUsage: [
    { week: 'Week 1', chat: 400, voice: 240 },
    { week: 'Week 2', chat: 300, voice: 139 },
    { week: 'Week 3', chat: 200, voice: 980 },
    { week: 'Week 4', chat: 278, voice: 390 },
  ],
  billingData: [
    { month: 'Jan', amount: 500 },
    { month: 'Feb', amount: 650 },
    { month: 'Mar', amount: 800 },
    { month: 'Apr', amount: 750 },
    { month: 'May', amount: 900 },
  ]
};

const VendorCard = ({ vendor }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <MotionCard whileHover={{ scale: 1.02 }} bg={cardBg} boxShadow="lg">
      <CardHeader>
        <Flex align="center" gap={4}>
          <Avatar name={vendor.name} />
          <Box>
            <Heading size="sm" color={headingColor}>{vendor.name}</Heading>
            <Badge colorScheme={vendor.status === 'active' ? 'green' : 'red'} mt={1}>
              {vendor.status}
            </Badge>
          </Box>
        </Flex>
      </CardHeader>
      <CardBody>
        <VStack align="start" spacing={4}>
          <Stat>
            <StatLabel color={textColor}>Revenue</StatLabel>
            <StatNumber color={headingColor}>${vendor.revenue.toLocaleString()}</StatNumber>
            <StatHelpText color={textColor}>
              <StatArrow type={vendor.growth >= 0 ? 'increase' : 'decrease'} />
              {Math.abs(vendor.growth)}%
            </StatHelpText>
          </Stat>
          
          <Box w="100%">
            <Text fontSize="sm" mb={2} color={textColor}>User Growth</Text>
            <Progress value={vendor.users} max={200} colorScheme="purple" />
            <Text fontSize="xs" mt={1} color={textColor}>{vendor.users} users</Text>
          </Box>
          
          <Box w="100%">
            <Text fontSize="sm" mb={2} color={textColor}>Product Count</Text>
            <Progress value={vendor.products} max={50} colorScheme="blue" />
            <Text fontSize="xs" mt={1} color={textColor}>{vendor.products} products</Text>
          </Box>
          
          <Text fontSize="sm" color={textColor}>
            Last Active: {new Date(vendor.lastActivity).toLocaleString()}
          </Text>
        </VStack>
      </CardBody>
    </MotionCard>
  );
};

const AdminCard = ({ admin }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  
  return (
    <MotionCard whileHover={{ scale: 1.02 }} bg={cardBg} boxShadow="lg">
      <CardHeader>
        <Flex align="center" gap={4}>
          <Avatar name={admin.name} />
          <Box>
            <Heading size="sm" color={headingColor}>{admin.name}</Heading>
            <Badge colorScheme="purple" mt={1}>
              {admin.role}
            </Badge>
          </Box>
        </Flex>
      </CardHeader>
      <CardBody>
        <VStack align="start" spacing={4}>
          <Stat>
            <StatLabel color={textColor}>Managed Vendors</StatLabel>
            <StatNumber color={headingColor}>{admin.managedVendors}</StatNumber>
          </Stat>
          
          <Box w="100%">
            <Text fontSize="sm" mb={2} color={textColor}>Performance</Text>
            <Progress value={admin.performance} colorScheme="green" />
            <Text fontSize="xs" mt={1} color={textColor}>{admin.performance}%</Text>
          </Box>
          
          <Text fontSize="sm" color={textColor}>
            Active Users: {admin.activeUsers}
          </Text>
          
          <Text fontSize="sm" color={textColor}>
            Last Active: {new Date(admin.lastActive).toLocaleString()}
          </Text>
        </VStack>
      </CardBody>
    </MotionCard>
  );
};

const UserStatisticsCard = ({ user }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Card bg={cardBg} boxShadow="lg">
      <CardHeader>
        <Heading size="md" color={headingColor}>User Statistics</Heading>
      </CardHeader>
      <CardBody>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box>
            <Stat>
              <StatLabel color={textColor}>Total Tokens</StatLabel>
              <StatNumber color={headingColor}>{user.tokens?.total || 0}</StatNumber>
              <StatHelpText color={textColor}>
                <StatArrow type="increase" />
                {user.tokens?.increase || 0}%
              </StatHelpText>
            </Stat>
          </Box>
          <Box>
            <Stat>
              <StatLabel color={textColor}>Remaining Tokens</StatLabel>
              <StatNumber color={headingColor}>{user.tokens?.remaining || 0}</StatNumber>
              <Progress value={(user.tokens?.remaining / user.tokens?.total) * 100} colorScheme="blue" />
            </Stat>
          </Box>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
};

const dummyStats = {
  totalAdmins: 24,
  totalTokens: 1000000,
  activeAdmins: 20,
  inactiveAdmins: 4,
};

const dummyAdmins = [
  { 
    id: 1, 
    name: 'John Smith', 
    email: 'john.smith@email.com', 
    club: 'Tech Club Alpha', 
    status: 'active', 
    totalTokens: 50000,
    lastActive: '2 minutes ago' 
  },
  { 
    id: 2, 
    name: 'Sarah Johnson', 
    email: 'sarah.j@techclub.com', 
    club: 'Sports Club Beta', 
    status: 'active', 
    totalTokens: 75000,
    lastActive: '1 hour ago' 
  },
  // Add more dummy data as needed
];

const ChartWrapper = ({ children, height = 300 }) => (
  <Box width="100%" height={height} minHeight={height} minWidth={0}>
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </Box>
);

const SuperAdminDashboard = () => {
  const { isSuperAdmin } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'whiteAlpha.900');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const statCardBg = useColorModeValue('gray.100', 'gray.900');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [apiKey, setApiKey] = useState('key_e601a8a9eccc7bc8bdd432513499');
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isTokenModalOpen, onOpen: onTokenModalOpen, onClose: onTokenModalClose } = useDisclosure();
  const { isOpen: isVendorModalOpen, onOpen: onVendorModalOpen, onClose: onVendorModalClose } = useDisclosure();
  const toast = useToast();
  const [admins, setAdmins] = useState(dummyAdmins);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [tokenAmount, setTokenAmount] = useState(0);
  const [isAddingTokens, setIsAddingTokens] = useState(true);
  const { isOpen: isNotificationModalOpen, onOpen: onNotificationModalOpen, onClose: onNotificationModalClose } = useDisclosure();
  const [notificationRecipient, setNotificationRecipient] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendAsEmail, setSendAsEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [newAdmin, setNewAdmin] = useState({
    name: '',
    description: '',
    email: '',
    password: '',
    confirmPassword: '',
    features: {
      voiceChat: false,
      chatbot: false
    },
    voiceCredentials: {
      apiKey: '',
      agentId: '',
      projectId: ''
    },
    chatCredentials: {
      apiKey: '',
      agentId: ''
    },
    totalTokens: 10000,
    status: 'active'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (feature) => {
    setNewAdmin(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const handleCredentialChange = (type, field, value) => {
    setNewAdmin(prev => ({
      ...prev,
      [`${type}Credentials`]: {
        ...prev[`${type}Credentials`],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newAdmin.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate password length
    if (newAdmin.password.length < 8) {
      toast({
        title: 'Invalid Password',
        description: 'Password must be at least 8 characters long',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate password match
    if (newAdmin.password !== newAdmin.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Create new admin
    const adminToAdd = {
      id: admins.length + 1,
      name: newAdmin.name,
      email: newAdmin.email,
      club: newAdmin.name,
      status: newAdmin.status,
      totalTokens: newAdmin.totalTokens,
      lastActive: 'Just now',
      features: newAdmin.features,
      credentials: {
        voice: newAdmin.voiceCredentials,
        chat: newAdmin.chatCredentials
      }
    };

    setAdmins([...admins, adminToAdd]);
    
    toast({
      title: 'Admin Created',
      description: 'New admin has been created successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });

    // Reset form and close modal
    setNewAdmin({
      name: '',
      description: '',
      email: '',
      password: '',
      confirmPassword: '',
      features: {
        voiceChat: false,
        chatbot: false
      },
      voiceCredentials: {
        apiKey: '',
        agentId: '',
        projectId: ''
      },
      chatCredentials: {
        apiKey: '',
        agentId: ''
      },
      totalTokens: 10000,
      status: 'active'
    });
    onClose();
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    onUserModalOpen();
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    onEditModalOpen();
  };

  const handleUpdateAdmin = (e) => {
    e.preventDefault();
    setAdmins(admins.map(admin => 
      admin.id === selectedAdmin.id ? selectedAdmin : admin
    ));
    toast({
      title: 'Admin Updated',
      description: 'Admin information has been updated successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onEditModalClose();
    setSelectedAdmin(null);
  };

  const handleDeleteAdmin = (adminId) => {
    setAdmins(admins.filter(admin => admin.id !== adminId));
    toast({
      title: 'Admin Deleted',
      description: 'Admin has been deleted successfully',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleTokenChange = (adminId, amount, isAdding) => {
    setAdmins(admins.map(admin => {
      if (admin.id === adminId) {
        const newTokens = isAdding 
          ? admin.totalTokens + amount 
          : Math.max(0, admin.totalTokens - amount);
        return { ...admin, totalTokens: newTokens };
      }
      return admin;
    }));
    toast({
      title: isAdding ? 'Tokens Added' : 'Tokens Deducted',
      description: `${amount} tokens have been ${isAdding ? 'added to' : 'deducted from'} the admin`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onTokenModalClose();
    setTokenAmount(0);
  };

  const handleSendNotification = (e) => {
    e.preventDefault();
    // Implement the logic to send the notification
    let description = 'Notification has been sent successfully';
    if (notificationRecipient !== 'all') {
      const recipientAdmin = dummyAdmins.find(admin => admin.email === notificationRecipient);
      if (recipientAdmin) {
         description = `Notification sent to ${recipientAdmin.name}`;
      }
    } else {
       description = `Notification sent to ${notificationRecipient}`;
    }
    if (sendAsEmail) {
      description += ' and email notification sent';
    }

    toast({
      title: 'Notification Sent',
      description: description,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onNotificationModalClose();
    // Optionally reset form fields
    setNotificationRecipient('');
    setNotificationMessage('');
    setSendAsEmail(false);
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = 
      admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.club.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || admin.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Add new admin-specific stats
  const adminStats = {
    totalAdmins: 24,
    totalTokens: 1000000,
    activeAdmins: 20,
    inactiveAdmins: 4,
    totalVoiceAgents: 15,
    totalChatAgents: 18,
    totalRevenue: 25000,
    pendingApprovals: 5
  };

  const tabs = ['overview', 'voice-agents', 'chat-agents', 'approvals'];

  // Add admin-specific widget data
  const adminWidgetData = {
    quickStats: {
      totalAdmins: 24,
      activeAdmins: 20,
      pendingApprovals: 5,
      totalRevenue: 25000
    },
    recentActivity: [
      { type: 'admin_created', user: 'John Doe', time: '2 hours ago', details: 'Created new admin account' },
      { type: 'agent_updated', user: 'Sarah Smith', time: '4 hours ago', details: 'Updated voice agent settings' },
      { type: 'approval_pending', user: 'Mike Johnson', time: '1 day ago', details: 'New admin approval request' }
    ],
    systemHealth: {
      voiceAgents: { status: 'healthy', uptime: '99.9%' },
      chatAgents: { status: 'healthy', uptime: '99.8%' },
      apiServices: { status: 'healthy', uptime: '99.95%' }
    }
  };

  if (!isSuperAdmin()) {
    return (
      <PageLayout title="Access Denied">
        <Text>You do not have permission to view this page.</Text>
      </PageLayout>
    );
  }

  return (
    <Box minH="100vh" bg={useColorModeValue('coolGray.50', 'gray.900')}>
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <Flex alignItems="center">
            <Box>
              <Heading size="xl" color={headingColor}>Super Admin Dashboard</Heading>
              <Text color={textColor} mt={1}>Manage admins and token distribution</Text>
            </Box>
            <Spacer />
            <HStack spacing={4}>
              <Button leftIcon={<FiPlus />} colorScheme="blue" size="md" onClick={onOpen}>
                Create Admin
              </Button>
              <Button leftIcon={<FiMessageSquare />} colorScheme="purple" size="md" onClick={onNotificationModalOpen}>
                Send Notification
              </Button>
            </HStack>
          </Flex>

          <Tabs variant="enclosed" colorScheme="blue" onChange={(index) => {
            setActiveTab(tabs[index]);
          }}>
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Voice Agents</Tab>
              <Tab>Chat Agents</Tab>
              <Tab>Approvals</Tab>
              <Tab>My Widgets</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mt={4}>
            <Card bg={statCardBg} boxShadow="md">
              <CardBody p={6}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>Total Admins</Text>
                          <Text fontSize="3xl" fontWeight="bold" color={headingColor}>{adminStats.totalAdmins}</Text>
                          <Text fontSize="xs" color={textColor}>Active: {adminStats.activeAdmins}</Text>
                  </Box>
                  <IconButton
                    icon={<Users />}
                    size="sm"
                    color="blue.500"
                    variant="ghost"
                  />
                </Flex>
              </CardBody>
            </Card>

            <Card bg={statCardBg} boxShadow="md">
              <CardBody p={6}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Box>
                          <Text fontSize="sm" fontWeight="medium" color={textColor}>Total Agents</Text>
                          <Text fontSize="3xl" fontWeight="bold" color="purple.500">
                            {adminStats.totalVoiceAgents + adminStats.totalChatAgents}
                          </Text>
                          <Text fontSize="xs" color={textColor}>
                            Voice: {adminStats.totalVoiceAgents} | Chat: {adminStats.totalChatAgents}
                          </Text>
                  </Box>
                  <IconButton
                    icon={<Shield />}
                    size="sm"
                          color="purple.500"
                    variant="ghost"
                  />
                </Flex>
              </CardBody>
            </Card>

            <Card bg={statCardBg} boxShadow="md">
              <CardBody p={6}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Box>
                          <Text fontSize="sm" fontWeight="medium" color={textColor}>Total Revenue</Text>
                          <Text fontSize="3xl" fontWeight="bold" color="green.500">
                            ${adminStats.totalRevenue.toLocaleString()}
                          </Text>
                          <Text fontSize="xs" color={textColor}>Monthly Revenue</Text>
                  </Box>
                  <IconButton
                          icon={<Settings />}
                    size="sm"
                          color="green.500"
                    variant="ghost"
                  />
                </Flex>
              </CardBody>
            </Card>

            <Card bg={statCardBg} boxShadow="md">
              <CardBody p={6}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Box>
                          <Text fontSize="sm" fontWeight="medium" color={textColor}>Pending Approvals</Text>
                          <Text fontSize="3xl" fontWeight="bold" color="orange.500">
                            {adminStats.pendingApprovals}
                          </Text>
                          <Text fontSize="xs" color={textColor}>Requires Action</Text>
                  </Box>
                  <IconButton
                          icon={<UserCheck />}
                    size="sm"
                          color="orange.500"
                    variant="ghost"
                  />
                </Flex>
              </CardBody>
            </Card>
          </SimpleGrid>

                {/* Admin-specific charts */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={6}>
          <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>Admin Performance</Heading>
                    </CardHeader>
                    <CardBody>
                      <ChartWrapper>
                        <BarChart data={[
                          { name: 'Voice Agents', active: 12, inactive: 3 },
                          { name: 'Chat Agents', active: 15, inactive: 3 },
                          { name: 'Total Admins', active: 20, inactive: 4 }
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke={useColorModeValue('gray.200', 'gray.700')} />
                          <XAxis dataKey="name" stroke={textColor} />
                          <YAxis stroke={textColor} />
                          <Tooltip contentStyle={{ background: cardBg, color: textColor, border: 'none' }} />
                          <Legend />
                          <Bar dataKey="active" fill="#48BB78" name="Active" />
                          <Bar dataKey="inactive" fill="#F56565" name="Inactive" />
                        </BarChart>
                      </ChartWrapper>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>Revenue Distribution</Heading>
                    </CardHeader>
                    <CardBody>
                      <ChartWrapper>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Voice Services', value: 15000 },
                              { name: 'Chat Services', value: 10000 }
                            ]}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {[
                              { name: 'Voice Services', value: 15000 },
                              { name: 'Chat Services', value: 10000 }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ background: cardBg, color: textColor, border: 'none' }} />
                          <Legend />
                        </PieChart>
                      </ChartWrapper>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* Admin List Card */}
                <Card bg={cardBg} boxShadow="lg" mt={6}>
            <CardHeader>
              <Flex justifyContent="space-between" alignItems="center">
                <Heading size="md" color={headingColor}>Admin List</Heading>
                <HStack spacing={2}>
                  <InputGroup size="sm" width="200px">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<FiSearch color={useColorModeValue('gray.400', 'gray.600')} />}
                    />
                    <Input
                      type="text"
                      placeholder="Search admins"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      borderRadius="md"
                       bg={useColorModeValue('gray.100', 'gray.700')}
                      borderColor={useColorModeValue('gray.200', 'gray.600')}
                      color={textColor}
                       _hover={{ borderColor: useColorModeValue('blue.300', 'blue.600') }}
                       _focus={{ borderColor: useColorModeValue('blue.500', 'blue.500'), boxShadow: 'outline' }}
                    />
                  </InputGroup>
                   <Select
                     placeholder="Filter by status"
                     size="sm"
                     width="150px"
                     value={statusFilter}
                     onChange={(e) => setStatusFilter(e.target.value)}
                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                     bg={useColorModeValue('gray.100', 'gray.700')}
                     color={textColor}
                      _hover={{ borderColor: useColorModeValue('blue.300', 'blue.600') }}
                      _focus={{ borderColor: useColorModeValue('blue.500', 'blue.500'), boxShadow: 'outline' }}
                   >
                     <option value="all">All Statuses</option>
                     <option value="active">Active</option>
                     <option value="inactive">Inactive</option>
                   </Select>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th color={textColor}>Admin</Th>
                      <Th color={textColor}>Status</Th>
                      <Th color={textColor}>Total Tokens</Th>
                       <Th color={textColor}>Last Active</Th>
                       <Th color={textColor}>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredAdmins.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" color={textColor}>
                          No admins found.
                        </Td>
                      </Tr>
                    ) : (
                      filteredAdmins.map(admin => (
                        <Tr key={admin.id} _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
                           <Td py={4} px={4}>
                             <VStack align="start" spacing={0}>
                               <Text fontWeight="bold" color={headingColor}>{admin.name}</Text>
                               <Text fontSize="sm" color={textColor}>{admin.email}</Text>
                             </VStack>
                           </Td>
                           <Td py={4} px={4}>
                              <Badge colorScheme={admin.status === 'active' ? 'green' : 'red'} fontSize="xs">
                                {admin.status}
                              </Badge>
                           </Td>
                          <Td py={4} px={4} color={textColor}>{admin.totalTokens.toLocaleString()}</Td>
                          <Td py={4} px={4} color={textColor}>{admin.lastActive}</Td>
                          <Td py={4} px={4}>
                            <HStack spacing={2}>
                              <Tooltip label="Add Tokens" hasArrow>
                                <IconButton
                                  aria-label="Add Tokens"
                                  icon={<FiPlusCircle />}
                                  size="sm"
                                  colorScheme="green"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAdmin(admin);
                                    setIsAddingTokens(true);
                                    onTokenModalOpen();
                                  }}
                                />
                              </Tooltip>
                               <Tooltip label="Deduct Tokens" hasArrow>
                                <IconButton
                                  aria-label="Deduct Tokens"
                                  icon={<FiMinusCircle />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAdmin(admin);
                                    setIsAddingTokens(false);
                                    onTokenModalOpen();
                                  }}
                                />
                               </Tooltip>
                                <Tooltip label="Edit Admin" hasArrow>
                                   <IconButton
                                     aria-label="Edit Admin"
                                     icon={<FiEdit2 />}
                                     size="sm"
                                     colorScheme="blue"
                                     variant="ghost"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       handleEditAdmin(admin);
                                     }}
                                   />
                                </Tooltip>
                                <Tooltip label="Delete Admin" hasArrow>
                                  <IconButton
                                    aria-label="Delete Admin"
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteAdmin(admin.id);
                                    }}
                                  />
                                </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
              </TabPanel>

              <TabPanel>
            <Card bg={cardBg} boxShadow="lg">
              <CardHeader>
                    <Heading size="md" color={headingColor}>Voice Agents</Heading>
              </CardHeader>
              <CardBody>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th color={textColor}>Agent Name</Th>
                          <Th color={textColor}>Status</Th>
                          <Th color={textColor}>Usage</Th>
                          <Th color={textColor}>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {admins.filter(admin => admin.features?.voiceChat).map(admin => (
                          <Tr key={admin.id}>
                            <Td color={textColor}>{admin.name}</Td>
                            <Td>
                              <Badge colorScheme={admin.status === 'active' ? 'green' : 'red'}>
                                {admin.status}
                              </Badge>
                            </Td>
                            <Td color={textColor}>0 calls</Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  aria-label="Edit Voice Agent"
                                  icon={<FiEdit2 />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => handleEditAdmin(admin)}
                                />
                                <IconButton
                                  aria-label="Delete Voice Agent"
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleDeleteAdmin(admin.id)}
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
              </CardBody>
            </Card>
              </TabPanel>

              <TabPanel>
            <Card bg={cardBg} boxShadow="lg">
              <CardHeader>
                    <Heading size="md" color={headingColor}>Chat Agents</Heading>
              </CardHeader>
              <CardBody>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th color={textColor}>Agent Name</Th>
                          <Th color={textColor}>Status</Th>
                          <Th color={textColor}>Usage</Th>
                          <Th color={textColor}>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {admins.filter(admin => admin.features?.chatbot).map(admin => (
                          <Tr key={admin.id}>
                            <Td color={textColor}>{admin.name}</Td>
                            <Td>
                              <Badge colorScheme={admin.status === 'active' ? 'green' : 'red'}>
                                {admin.status}
                              </Badge>
                            </Td>
                            <Td color={textColor}>0 messages</Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  aria-label="Edit Chat Agent"
                                  icon={<FiEdit2 />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => handleEditAdmin(admin)}
                                />
                                <IconButton
                                  aria-label="Delete Chat Agent"
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleDeleteAdmin(admin.id)}
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
              </CardBody>
            </Card>
              </TabPanel>

              <TabPanel>
             <Card bg={cardBg} boxShadow="lg">
               <CardHeader>
                    <Heading size="md" color={headingColor}>Pending Approvals</Heading>
               </CardHeader>
               <CardBody>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th color={textColor}>Request Type</Th>
                          <Th color={textColor}>Requester</Th>
                          <Th color={textColor}>Date</Th>
                          <Th color={textColor}>Status</Th>
                          <Th color={textColor}>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td color={textColor}>New Admin Account</Td>
                          <Td color={textColor}>John Doe</Td>
                          <Td color={textColor}>2024-03-21</Td>
                          <Td>
                            <Badge colorScheme="yellow">Pending</Badge>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button size="sm" colorScheme="green">Approve</Button>
                              <Button size="sm" colorScheme="red">Reject</Button>
                            </HStack>
                          </Td>
                        </Tr>
                        {/* Add more approval rows as needed */}
                      </Tbody>
                    </Table>
               </CardBody>
             </Card>
              </TabPanel>

              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {/* Quick Stats Widget */}
             <Card bg={cardBg} boxShadow="lg">
                <CardHeader>
                      <Heading size="md" color={headingColor}>Quick Stats</Heading>
                </CardHeader>
                <CardBody>
                      <VStack spacing={4} align="stretch">
                        <HStack justify="space-between">
                          <Text color={textColor}>Total Admins</Text>
                          <Text fontWeight="bold" color={headingColor}>{adminWidgetData.quickStats.totalAdmins}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text color={textColor}>Active Admins</Text>
                          <Text fontWeight="bold" color="green.500">{adminWidgetData.quickStats.activeAdmins}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text color={textColor}>Pending Approvals</Text>
                          <Text fontWeight="bold" color="orange.500">{adminWidgetData.quickStats.pendingApprovals}</Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text color={textColor}>Total Revenue</Text>
                          <Text fontWeight="bold" color="blue.500">${adminWidgetData.quickStats.totalRevenue.toLocaleString()}</Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Recent Activity Widget */}
                  <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>Recent Activity</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        {adminWidgetData.recentActivity.map((activity, index) => (
                          <Box key={index} p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                            <HStack justify="space-between">
                              <Text fontWeight="bold" color={headingColor}>{activity.user}</Text>
                              <Text fontSize="sm" color={textColor}>{activity.time}</Text>
                            </HStack>
                            <Text color={textColor}>{activity.details}</Text>
                          </Box>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* System Health Widget */}
                  <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>System Health</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        {Object.entries(adminWidgetData.systemHealth).map(([service, data]) => (
                          <Box key={service} p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                            <HStack justify="space-between">
                              <Text fontWeight="bold" color={headingColor}>
                                {service.charAt(0).toUpperCase() + service.slice(1)}
                              </Text>
                              <Badge colorScheme={data.status === 'healthy' ? 'green' : 'red'}>
                                {data.status}
                              </Badge>
                            </HStack>
                            <Text color={textColor}>Uptime: {data.uptime}</Text>
                          </Box>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Admin Actions Widget */}
                  <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>Quick Actions</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={3}>
                        <Button
                          leftIcon={<FiPlus />}
                          colorScheme="blue"
                          width="full"
                          onClick={onOpen}
                        >
                          Create New Admin
                        </Button>
                        <Button
                          leftIcon={<FiMessageSquare />}
                          colorScheme="purple"
                          width="full"
                          onClick={onNotificationModalOpen}
                        >
                          Send Notification
                        </Button>
                        <Button
                          leftIcon={<FiEdit2 />}
                          colorScheme="green"
                          width="full"
                          onClick={() => setActiveTab('approvals')}
                        >
                          View Pending Approvals
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* API Status Widget */}
                  <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>API Status</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <HStack justify="space-between">
                          <Text color={textColor}>Voice API</Text>
                          <Badge colorScheme="green">Active</Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text color={textColor}>Chat API</Text>
                          <Badge colorScheme="green">Active</Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text color={textColor}>Authentication</Text>
                          <Badge colorScheme="green">Active</Badge>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>

                  {/* Token Distribution Widget */}
                  <Card bg={cardBg} boxShadow="lg">
                    <CardHeader>
                      <Heading size="md" color={headingColor}>Token Distribution</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <Box>
                          <Text color={textColor} mb={2}>Voice Agents</Text>
                          <Progress value={75} colorScheme="blue" />
                          <Text fontSize="sm" color={textColor} mt={1}>75% of total tokens</Text>
                        </Box>
                        <Box>
                          <Text color={textColor} mb={2}>Chat Agents</Text>
                          <Progress value={25} colorScheme="purple" />
                          <Text fontSize="sm" color={textColor} mt={1}>25% of total tokens</Text>
                        </Box>
                      </VStack>
                </CardBody>
             </Card>
          </SimpleGrid>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent bg={cardBg}>
              <ModalHeader color={headingColor}>Create Admin</ModalHeader>
              <ModalCloseButton color={textColor} />
              <ModalBody pb={6}>
                <VStack spacing={4} as="form" onSubmit={handleSubmit}>
                  <FormControl isRequired>
                    <FormLabel color={textColor}>Name</FormLabel>
                    <Input
                      name="name"
                      placeholder="Enter admin name"
                      value={newAdmin.name}
                      onChange={handleInputChange}
                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                       bg={useColorModeValue('gray.100', 'gray.700')}
                       color={textColor}
                    />
                  </FormControl>
                   <FormControl isRequired>
                    <FormLabel color={textColor}>Email</FormLabel>
                     <Input
                      name="email"
                      type="email"
                      placeholder="Enter admin email"
                       value={newAdmin.email}
                      onChange={handleInputChange}
                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                       bg={useColorModeValue('gray.100', 'gray.700')}
                       color={textColor}
                     />
                   </FormControl>
                    <FormControl isRequired>
                     <FormLabel color={textColor}>Password</FormLabel>
                      <Input
                       name="password"
                       type="password"
                       placeholder="Enter password"
                        value={newAdmin.password}
                       onChange={handleInputChange}
                        borderColor={useColorModeValue('gray.200', 'gray.600')}
                       bg={useColorModeValue('gray.100', 'gray.700')}
                        color={textColor}
                      />
                    </FormControl>
                    <FormControl isRequired>
                     <FormLabel color={textColor}>Confirm Password</FormLabel>
                      <Input
                       name="confirmPassword"
                       type="password"
                       placeholder="Confirm password"
                        value={newAdmin.confirmPassword}
                       onChange={handleInputChange}
                        borderColor={useColorModeValue('gray.200', 'gray.600')}
                       bg={useColorModeValue('gray.100', 'gray.700')}
                        color={textColor}
                      />
                    </FormControl>
                     <FormControl>
                       <FormLabel color={textColor}>Total Tokens</FormLabel>
                        <NumberInput
                          name="totalTokens"
                          defaultValue={10000}
                          min={0}
                          onChange={(valueString) => handleInputChange({ target: { name: 'totalTokens', value: parseInt(valueString, 10) } })}
                        >
                          <NumberInputField 
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            bg={useColorModeValue('gray.100', 'gray.700')}
                            color={textColor}
                          />
                          <NumberInputStepper>
                            <NumberIncrementStepper color={textColor} />
                            <NumberDecrementStepper color={textColor} />
                          </NumberInputStepper>
                        </NumberInput>
                     </FormControl>
                     <FormControl>
                       <FormLabel color={textColor}>Status</FormLabel>
                        <Select
                          name="status"
                          value={newAdmin.status}
                          onChange={handleInputChange}
                           borderColor={useColorModeValue('gray.200', 'gray.600')}
                          bg={useColorModeValue('gray.100', 'gray.700')}
                           color={textColor}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Select>
                     </FormControl>

                     <Box w="full">
                       <Text fontWeight="bold" mb={2} color={headingColor}>Features</Text>
                       <VStack align="start" spacing={2}>
                          <Checkbox 
                            isChecked={newAdmin.features.voiceChat}
                            onChange={() => handleFeatureChange('voiceChat')}
                             colorScheme="blue"
                             color={textColor}
                          >
                            Voice Chat
                          </Checkbox>
                          <Checkbox 
                            isChecked={newAdmin.features.chatbot}
                            onChange={() => handleFeatureChange('chatbot')}
                             colorScheme="blue"
                             color={textColor}
                          >
                            Chatbot
                          </Checkbox>
                       </VStack>
                     </Box>

                      {newAdmin.features.voiceChat && (
                        <Box w="full">
                           <Text fontWeight="bold" mb={2} color={headingColor}>Voice Credentials</Text>
                           <VStack spacing={3}>
                              <FormControl isRequired>
                                 <FormLabel color={textColor}>API Key</FormLabel>
                                 <Input 
                                    placeholder="Enter voice API Key"
                                    value={newAdmin.voiceCredentials.apiKey}
                                    onChange={(e) => handleCredentialChange('voice', 'apiKey', e.target.value)}
                                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                                    bg={useColorModeValue('gray.100', 'gray.700')}
                                     color={textColor}
                                 />
                              </FormControl>
                               <FormControl isRequired>
                                 <FormLabel color={textColor}>Agent ID</FormLabel>
                                 <Input 
                                    placeholder="Enter voice Agent ID"
                                     value={newAdmin.voiceCredentials.agentId}
                                    onChange={(e) => handleCredentialChange('voice', 'agentId', e.target.value)}
                                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                                    bg={useColorModeValue('gray.100', 'gray.700')}
                                     color={textColor}
                                 />
                              </FormControl>
                               <FormControl isRequired>
                                 <FormLabel color={textColor}>Project ID</FormLabel>
                                 <Input 
                                    placeholder="Enter voice Project ID"
                                     value={newAdmin.voiceCredentials.projectId}
                                    onChange={(e) => handleCredentialChange('voice', 'projectId', e.target.value)}
                                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                                    bg={useColorModeValue('gray.100', 'gray.700')}
                                     color={textColor}
                                 />
                              </FormControl>
                           </VStack>
                        </Box>
                      )}

                       {newAdmin.features.chatbot && (
                        <Box w="full">
                           <Text fontWeight="bold" mb={2} color={headingColor}>Chat Credentials</Text>
                           <VStack spacing={3}>
                              <FormControl isRequired>
                                 <FormLabel color={textColor}>API Key</FormLabel>
                                 <Input 
                                    placeholder="Enter chat API Key"
                                     value={newAdmin.chatCredentials.apiKey}
                                    onChange={(e) => handleCredentialChange('chat', 'apiKey', e.target.value)}
                                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                                    bg={useColorModeValue('gray.100', 'gray.700')}
                                     color={textColor}
                                 />
                              </FormControl>
                               <FormControl isRequired>
                                 <FormLabel color={textColor}>Agent ID</FormLabel>
                                 <Input 
                                    placeholder="Enter chat Agent ID"
                                     value={newAdmin.chatCredentials.agentId}
                                    onChange={(e) => handleCredentialChange('chat', 'agentId', e.target.value)}
                                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                                    bg={useColorModeValue('gray.100', 'gray.700')}
                                     color={textColor}
                                 />
                              </FormControl>
                           </VStack>
                        </Box>
                       )}

                  <Button type="submit" colorScheme="blue" width="full">
                    Create Admin
                  </Button>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

           {/* Edit Admin Modal */}
          <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="lg">
             <ModalOverlay />
             <ModalContent bg={cardBg}>
               <ModalHeader color={headingColor}>Edit Admin</ModalHeader>
               <ModalCloseButton color={textColor} />
               <ModalBody pb={6}>
                 {selectedAdmin && (
                   <VStack spacing={4} as="form" onSubmit={handleUpdateAdmin}>
                      <FormControl isRequired>
                        <FormLabel color={textColor}>Name</FormLabel>
                         <Input
                           name="name"
                           value={selectedAdmin.name}
                           onChange={(e) => setSelectedAdmin({...selectedAdmin, name: e.target.value})}
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                           bg={useColorModeValue('gray.100', 'gray.700')}
                            color={textColor}
                         />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel color={textColor}>Email</FormLabel>
                         <Input
                           name="email"
                           type="email"
                           value={selectedAdmin.email}
                           onChange={(e) => setSelectedAdmin({...selectedAdmin, email: e.target.value})}
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                           bg={useColorModeValue('gray.100', 'gray.700')}
                            color={textColor}
                         />
                      </FormControl>
                       <FormControl>
                         <FormLabel color={textColor}>Total Tokens</FormLabel>
                          <NumberInput
                            name="totalTokens"
                            value={selectedAdmin.totalTokens}
                             onChange={(valueString) => setSelectedAdmin({...selectedAdmin, totalTokens: parseInt(valueString, 10)})}
                            min={0}
                          >
                            <NumberInputField 
                               borderColor={useColorModeValue('gray.200', 'gray.600')}
                              bg={useColorModeValue('gray.100', 'gray.700')}
                               color={textColor}
                            />
                            <NumberInputStepper>
                              <NumberIncrementStepper color={textColor} />
                              <NumberDecrementStepper color={textColor} />
                            </NumberInputStepper>
                          </NumberInput>
                       </FormControl>
                        <FormControl isRequired>
                         <FormLabel color={textColor}>Status</FormLabel>
                          <Select
                            name="status"
                            value={selectedAdmin.status}
                            onChange={(e) => setSelectedAdmin({...selectedAdmin, status: e.target.value})}
                             borderColor={useColorModeValue('gray.200', 'gray.600')}
                            bg={useColorModeValue('gray.100', 'gray.700')}
                             color={textColor}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Select>
                       </FormControl>

                       <Box w="full">
                         <Text fontWeight="bold" mb={2} color={headingColor}>Features</Text>
                         <VStack align="start" spacing={2}>
                            <Checkbox 
                              isChecked={selectedAdmin.features?.voiceChat || false}
                              onChange={(e) => setSelectedAdmin({...selectedAdmin, features: {...selectedAdmin.features, voiceChat: e.target.checked}})}
                               colorScheme="blue"
                               color={textColor}
                            >
                              Voice Chat
                            </Checkbox>
                            <Checkbox 
                              isChecked={selectedAdmin.features?.chatbot || false}
                              onChange={(e) => setSelectedAdmin({...selectedAdmin, features: {...selectedAdmin.features, chatbot: e.target.checked}})}
                               colorScheme="blue"
                               color={textColor}
                            >
                              Chatbot
                            </Checkbox>
                         </VStack>
                       </Box>

                        {selectedAdmin.features?.voiceChat && (
                          <Box w="full">
                             <Text fontWeight="bold" mb={2} color={headingColor}>Voice Credentials</Text>
                             <VStack spacing={3}>
                                <FormControl isRequired>
                                   <FormLabel color={textColor}>API Key</FormLabel>
                                   <Input 
                                      placeholder="Enter voice API Key"
                                      value={selectedAdmin.credentials?.voice?.apiKey || ''}
                                       onChange={(e) => setSelectedAdmin({...selectedAdmin, credentials: {...selectedAdmin.credentials, voice: {...selectedAdmin.credentials?.voice, apiKey: e.target.value}}})}
                                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                                      bg={useColorModeValue('gray.100', 'gray.700')}
                                       color={textColor}
                                   />
                                </FormControl>
                                 <FormControl isRequired>
                                   <FormLabel color={textColor}>Agent ID</FormLabel>
                                   <Input 
                                      placeholder="Enter voice Agent ID"
                                       value={selectedAdmin.credentials?.voice?.agentId || ''}
                                      onChange={(e) => setSelectedAdmin({...selectedAdmin, credentials: {...selectedAdmin.credentials, voice: {...selectedAdmin.credentials?.voice, agentId: e.target.value}}})}
                                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                                      bg={useColorModeValue('gray.100', 'gray.700')}
                                       color={textColor}
                                   />
                                </FormControl>
                                 <FormControl isRequired>
                                   <FormLabel color={textColor}>Project ID</FormLabel>
                                   <Input 
                                      placeholder="Enter voice Project ID"
                                       value={selectedAdmin.credentials?.voice?.projectId || ''}
                                      onChange={(e) => setSelectedAdmin({...selectedAdmin, credentials: {...selectedAdmin.credentials, voice: {...selectedAdmin.credentials?.voice, projectId: e.target.value}}})}
                                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                                      bg={useColorModeValue('gray.100', 'gray.700')}
                                       color={textColor}
                                   />
                                </FormControl>
                             </VStack>
                          </Box>
                        )}

                         {selectedAdmin.features?.chatbot && (
                          <Box w="full">
                             <Text fontWeight="bold" mb={2} color={headingColor}>Chat Credentials</Text>
                             <VStack spacing={3}>
                                <FormControl isRequired>
                                   <FormLabel color={textColor}>API Key</FormLabel>
                                   <Input 
                                      placeholder="Enter chat API Key"
                                       value={selectedAdmin.credentials?.chat?.apiKey || ''}
                                      onChange={(e) => setSelectedAdmin({...selectedAdmin, credentials: {...selectedAdmin.credentials, chat: {...selectedAdmin.credentials?.chat, apiKey: e.target.value}}})}
                                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                                      bg={useColorModeValue('gray.100', 'gray.700')}
                                       color={textColor}
                                   />
                                </FormControl>
                                 <FormControl isRequired>
                                   <FormLabel color={textColor}>Agent ID</FormLabel>
                                   <Input 
                                      placeholder="Enter chat Agent ID"
                                       value={selectedAdmin.credentials?.chat?.agentId || ''}
                                      onChange={(e) => setSelectedAdmin({...selectedAdmin, credentials: {...selectedAdmin.credentials, chat: {...selectedAdmin.credentials?.chat, agentId: e.target.value}}})}
                                       borderColor={useColorModeValue('gray.200', 'gray.600')}
                                      bg={useColorModeValue('gray.100', 'gray.700')}
                                       color={textColor}
                                   />
                                </FormControl>
                             </VStack>
                          </Box>
                         )}

                     <FormControl isRequired display="flex" alignItems="center">
                       <FormLabel htmlFor="edit-admin-status" mb="0" color={textColor}>
                          Active
                       </FormLabel>
                       <Switch 
                          id="edit-admin-status"
                           isChecked={selectedAdmin.status === 'active'}
                          onChange={(e) => setSelectedAdmin({...selectedAdmin, status: e.target.checked ? 'active' : 'inactive'})}
                           colorScheme="blue"
                        />
                     </FormControl>

                     <Button type="submit" colorScheme="blue" width="full">
                       Update Admin
                     </Button>
                   </VStack>
                 )}
               </ModalBody>
            </ModalContent>
           </Modal>

           {/* Token Management Modal */}
            <Modal isOpen={isTokenModalOpen} onClose={onTokenModalClose} size="md">
             <ModalOverlay />
             <ModalContent bg={cardBg}>
               <ModalHeader color={headingColor}>{isAddingTokens ? 'Add Tokens' : 'Deduct Tokens'}</ModalHeader>
               <ModalCloseButton color={textColor} />
               <ModalBody pb={6}>
                 {selectedAdmin && (
                   <VStack spacing={4} as="form" onSubmit={(e) => {
                     e.preventDefault();
                     handleTokenChange(selectedAdmin.id, tokenAmount, isAddingTokens);
                   }}>
                     <Text color={textColor}>Admin: {selectedAdmin.name}</Text>
                      <FormControl isRequired>
                       <FormLabel color={textColor}>Amount</FormLabel>
                        <NumberInput
                          defaultValue={0}
                          min={0}
                          onChange={(valueString) => setTokenAmount(parseInt(valueString, 10))}
                        >
                          <NumberInputField 
                            borderColor={useColorModeValue('gray.200', 'gray.600')}
                            bg={useColorModeValue('gray.100', 'gray.700')}
                            color={textColor}
                          />
                          <NumberInputStepper>
                            <NumberIncrementStepper color={textColor} />
                            <NumberDecrementStepper color={textColor} />
                          </NumberInputStepper>
                        </NumberInput>
                     </FormControl>
                     <Button type="submit" colorScheme={isAddingTokens ? 'green' : 'red'} width="full">
                       {isAddingTokens ? 'Add Tokens' : 'Deduct Tokens'}
                     </Button>
                   </VStack>
                 )}
               </ModalBody>
             </ModalContent>
            </Modal>

           {/* Notification Modal */}
           <Modal isOpen={isNotificationModalOpen} onClose={onNotificationModalClose} size="lg">
             <ModalOverlay />
             <ModalContent bg={cardBg}>
               <ModalHeader color={headingColor}>Send Notification</ModalHeader>
               <ModalCloseButton color={textColor} />
               <ModalBody pb={6}>
                  <VStack spacing={4} as="form" onSubmit={handleSendNotification}>
                     <FormControl isRequired>
                       <FormLabel color={textColor}>Recipient</FormLabel>
                        <Select
                          placeholder="Select recipient"
                          value={notificationRecipient}
                          onChange={(e) => setNotificationRecipient(e.target.value)}
                           borderColor={useColorModeValue('gray.200', 'gray.600')}
                          bg={useColorModeValue('gray.100', 'gray.700')}
                           color={textColor}
                        >
                          <option value="all">All Admins</option>
                           {admins.map(admin => (
                             <option key={admin.id} value={admin.email}>{admin.name} ({admin.email})</option>
                           ))}
                        </Select>
                     </FormControl>
                      <FormControl isRequired>
                       <FormLabel color={textColor}>Message</FormLabel>
                        <Textarea
                          placeholder="Enter your message"
                          value={notificationMessage}
                          onChange={(e) => setNotificationMessage(e.target.value)}
                           borderColor={useColorModeValue('gray.200', 'gray.600')}
                          bg={useColorModeValue('gray.100', 'gray.700')}
                           color={textColor}
                        />
                     </FormControl>
                     <FormControl display="flex" alignItems="center">
                        <FormLabel htmlFor="send-as-email" mb="0" color={textColor}>
                          Send as Email
                        </FormLabel>
                        <Switch 
                           id="send-as-email" 
                           isChecked={sendAsEmail} 
                           onChange={(e) => setSendAsEmail(e.target.checked)}
                            colorScheme="blue"
                         />
                      </FormControl>
                     <Button type="submit" colorScheme="blue" width="full">
                       Send Notification
                     </Button>
                  </VStack>
               </ModalBody>
             </ModalContent>
           </Modal>

           {/* User Details Modal */}
           <UserDetailModal
              isOpen={isUserModalOpen}
              onClose={onUserModalClose}
              user={selectedUser}
           />

        </VStack>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard; 
